import React from 'react';
import {
  objectOf, shape, bool, string, func
} from 'prop-types';
import _map from 'lodash/map';
import _orderBy from 'lodash/orderBy';
import _isEmpty from 'lodash/isEmpty';
import _find from 'lodash/find';
import _keyBy from 'lodash/keyBy';
import _uniq from 'lodash/uniq';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { QuickView } from '@thd-olt-component-react/quick-view';
import { Typography, Alert } from '@one-thd/sui-atomic-components';
import { Carousel } from '@one-thd/sui-carousel';
import { Choice, BundleChoice, AccessoryChoice } from '../choice';
import {
  carouselBreakpointsShape, choiceShape, optionShape,
  metadataShape, productDataShape, bundleDataShape
} from '../types';
import { withTemplateData } from '../hoc';
import { ChoicePlaceholders } from '../../placeholders/ChoicePlaceholders';
import { useCompatibilityStore } from '../../store';
import { getEnhancedChoices } from '../utils';
import { CONFIGURATOR } from '../../constants';
import { optionIncludesNonViz } from './utils';

const Desktop = (props) => {
  const {
    option,
    choices,
    customCarouselBreakpoints,
    handleQuickView,
    metadata,
    productData,
    quickView,
    setQuickView,
    zipCode,
    bundles
  } = props;

  const disabled = useCompatibilityStore((state) => state.disabled);
  const enhancedChoices = getEnhancedChoices({
    option, metadata, productData, zipCode, disabled, choices, bundles
  });

  const sortedChoices = _orderBy((enhancedChoices), ['disabled', 'isAvailable'], ['asc', 'desc']);

  return (
    <div className="sui-block option">
      <div className="sui-block sui-pb-1">
        <Typography variant="body-base" weight="bold">{option.shortLabel}</Typography>
        {(optionIncludesNonViz(option, CONFIGURATOR.OPTIONS.NON_VISUALIZED_OPTION) && option?.longLabel)
          ? (
            <div className="sui-pt-2">
              <Alert status="info">{option?.longLabel}</Alert>
            </div>
          )
          : (
            <Typography variant="body-xs">{option.longLabel}</Typography>
          )}
      </div>
      {_isEmpty(choices) && (
        <ChoicePlaceholders />
      )}
      {!_isEmpty(choices) && (
        <Carousel
          SwiperProps={{ simulateTouch: false }}
          disableShadow
          loop={false}
          breakpoints={customCarouselBreakpoints}
        >
          {_map(sortedChoices, (choice) => {
            const {
              id = null,
              internalName = null,
              sku
            } = choice;
            const {
              BundleDesign: bundleDesign = null,
              ProductType: productType = null
            } = metadata?.[id]?.metadata;

            if (bundleDesign) {
              return (
                <div key={internalName}>
                  <BundleChoice
                    choice={choice}
                    quickViewHandler={handleQuickView}
                    choiceMetaData={metadata?.[id]?.metadata}
                    product={productData?.[sku]}
                    zipCode={zipCode}
                    bundle={bundles?.[bundleDesign]}
                    metadata={
                      _keyBy(
                        _map(
                          _uniq(bundles?.[bundleDesign]?.bundleChoices),
                          (choiceId) => metadata?.[choiceId]),
                        'choiceId'
                      )
                    }
                  />
                </div>
              );
            }

            if (productType === CONFIGURATOR.CHOICE.ACCESSORY) {
              return (
                <div key={internalName}>
                  <AccessoryChoice
                    choice={choice}
                    quickViewHandler={handleQuickView}
                    choiceMetaData={metadata?.[id]?.metadata}
                    product={productData?.[sku]}
                    zipCode={zipCode}
                  />
                </div>
              );
            }

            return (
              <div key={internalName}>
                <Choice
                  choice={choice}
                  quickViewHandler={handleQuickView}
                  choiceMetaData={metadata?.[id]?.metadata}
                  product={productData?.[sku]}
                  zipCode={zipCode}
                />
              </div>
            );
          })}
        </Carousel>
      )}
      {quickView?.itemId && (
        <QueryProvider cacheKey="quick-view-desktop">
          <QuickView
            itemId={quickView?.itemId}
            isOpen={quickView?.isOpen}
            onClose={() => setQuickView({ itemId: null, isOpen: false })}
          />
        </QueryProvider>
      )}
    </div>
  );
};

Desktop.propTypes = {
  option: optionShape.isRequired,
  quickView: shape({
    isOpen: bool,
    itemId: string,
  }).isRequired,
  choices: objectOf(choiceShape),
  zipCode: string,
  customCarouselBreakpoints: carouselBreakpointsShape.isRequired,
  handleQuickView: func.isRequired,
  setQuickView: func.isRequired,
  metadata: metadataShape,
  productData: objectOf(productDataShape),
  bundles: objectOf(bundleDataShape)
};

Desktop.defaultProps = {
  choices: [],
  zipCode: '30339',
  metadata: {},
  productData: {},
  bundles: {}
};

export default withTemplateData(Desktop);
